import { useNavigate, useSearch } from '@tanstack/react-router';
import { CalendarDays, Check, CornerUpLeft, FileSignature, Gavel, ListChecks, MessageSquareWarning, PencilLine, Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { jevBudgetLabel, type HmActionItem, type HmApprovalAction, type HmDecisionAction, type HmOfferApprovalAction, type HmRequestAction, type HmShortlistAction, type ModuleState } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { ToneChip, ToneIcon, toneCardClass, toneClass, type Tone } from '../../../components/Tone';
import { SkeletonLines } from '../../../components/Skeleton';
import {
  EmptyState,
  ForbiddenState,
  ServerErrorState,
} from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { formatIsoDate, formatRelative } from '../../../lib/format';
import { useModules } from '../../../lib/modules';
import { useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useActionTimer, useHmActions } from '../api/hm-api';
import {
  HM_ACTIONS_KEY,
  removeHmAction,
  useDecideApproval,
  useRequisitionAction,
} from '../api/requisitions-api';
import { DecisionDialog } from '../components/DecisionDialog';
import { ReasonDialog } from '../components/ReasonDialog';
import { PriorityDot, SlaFigure } from '../components/RequisitionBadges';
import styles from './MyActionsPage.module.css';

/**
 * HM-02 — everything waiting on this manager, overdue first (master plan S10).
 *
 * In S10 that is approvals routed to them, and their own requests that need a move:
 * a draft to finish (a backfill drafted from an exit lands here), one sent back, one
 * rejected. S21 adds rounds waiting on their decision (N-41) or tie-break (N-44); S23 the
 * offers waiting on their approval (E16), each opening the offer, where Approve is; R6 the
 * shortlists sent to them with no Done yet ("Waiting on you", §L31), each opening SHL-01.
 *
 * Judged in taps (master plan S10): approving is one, rejecting or sending back is
 * two plus the reason, fixing a returned request is two. Every item leaves the list
 * the moment it is acted on (§5a rule 1) and comes back only if the server refuses.
 */

export interface MyActionsSearch {
  /** N-01 / N-02: the approval step to open on. */
  readonly step?: string;
  /** N-05 / N-06: the request to open on. */
  readonly request?: string;
}

export function MyActionsPage(): ReactNode {
  const canView = useCanView('requisition');
  const search: MyActionsSearch = useSearch({ from: '/app-layout/my/actions' });
  const actions = useHmActions();
  const navigate = useNavigate();
  const modules = useModules();

  if (!canView) return <ForbiddenState resource="hiring requests" contact="Your admin" />;
  if (actions.isError) return <ServerErrorState onRetry={() => void actions.refetch()} />;

  const items = (actions.data ?? []).filter((item) => isItemShown(item, modules));
  const focusKey = search.step ? `approval:${search.step}` : search.request ? `request:${search.request}` : null;
  const isFocusGone = focusKey !== null && actions.data !== undefined && !items.some((item) => item.key === focusKey);
  const overdue = items.filter((item) => item.isOverdue).length;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>My actions</h1>
        <p className={styles.lead}>
          {items.length === 0
            ? 'Nothing is waiting on you.'
            : `${items.length} waiting on you${overdue > 0 ? `, ${overdue} overdue` : ''}. Overdue first.`}
        </p>
      </header>

      {isFocusGone && (
        <p className={`${toneClass} ${styles.gone}`} data-tone="done" role="status">
          The item that link pointed to has already been dealt with.
        </p>
      )}

      {actions.isPending ? (
        <SkeletonLines count={5} />
      ) : items.length === 0 ? (
        <EmptyState
          title="You're all caught up"
          body={
            modules.requisitions
              ? "Approvals, requests sent back to you, drafts to finish, shortlists to answer and interview rounds to decide appear here, and you're told the moment one arrives."
              : "Shortlists to answer and interview rounds to decide appear here, and you're told the moment one arrives."
          }
          action={
            modules.requisitions ? (
              <Button variant="solid" icon={Plus} onClick={() => void navigate({ to: '/request-hire' })}>
                Request a hire
              </Button>
            ) : (
              <Button variant="outline" icon={CalendarDays} onClick={() => void navigate({ to: '/interviews/mine' })}>
                My interviews
              </Button>
            )
          }
        />
      ) : (
        <ul className={styles.list}>
          {items.map((item) => (
            <ActionCard key={item.key} item={item} isFocused={item.key === focusKey} />
          ))}
        </ul>
      )}
    </div>
  );
}

/** R1 (§L34): an item from a switched-off module stays on file but is not asked of anyone. */
function isItemShown(item: HmActionItem, modules: ModuleState): boolean {
  switch (item.kind) {
    case 'approval':
      return modules.approvals;
    case 'offer_approval':
      return modules.offers;
    case 'decision':
    case 'shortlist':
      return true;
    default:
      return modules.requisitions;
  }
}

function ActionCard({ item, isFocused }: { readonly item: HmActionItem; readonly isFocused: boolean }): ReactNode {
  const ref = useRef<HTMLLIElement | null>(null);

  // A deep link lands on the item itself, not on the top of a list (non-negotiable #3).
  useEffect(() => {
    if (isFocused) ref.current?.scrollIntoView({ block: 'center' });
  }, [isFocused]);

  const tone = actionTone(item);
  return (
    <li ref={ref} className={`${styles.item} ${toneCardClass(true)}`} data-focused={isFocused} data-tone={tone}>
      <ToneIcon tone={tone} />
      <ActionBody item={item} />
    </li>
  );
}

/** Overdue wins; otherwise the part of hiring the item is. A rejected request is closed, never red (ADR 0035). */
function actionTone(item: HmActionItem): Tone {
  if (item.isOverdue) return 'urgent';
  switch (item.kind) {
    case 'decision':
      return 'interview';
    case 'offer_approval':
      return 'offer';
    case 'shortlist':
      return 'screening';
    case 'rejected':
      return 'rejected';
    default:
      return 'requisition';
  }
}

function ActionBody({ item }: { readonly item: HmActionItem }): ReactNode {
  switch (item.kind) {
    case 'approval':
      return <ApprovalBody item={item} />;
    case 'decision':
      return <DecisionBody item={item} />;
    case 'offer_approval':
      return <OfferApprovalBody item={item} />;
    case 'shortlist':
      return <ShortlistBody item={item} />;
    default:
      return <RequestBody item={item} />;
  }
}

function CardTop({ item, children }: { readonly item: HmApprovalAction | HmRequestAction; readonly children?: ReactNode }): ReactNode {
  return (
    <>
      <div className={styles.top}>
        <span className={styles.ref}>{item.reference}</span>
        <PriorityDot priority={item.priority} />
        {item.isOverdue && (
          <ToneChip tone="urgent">Overdue</ToneChip>
        )}
        {children}
      </div>
      <span className={styles.itemTitle}>{item.title}</span>
    </>
  );
}

// --- A round to decide (S21: N-41, N-44) ----------------------------------------------

function DecisionBody({ item }: { readonly item: HmDecisionAction }): ReactNode {
  const navigate = useNavigate();
  return (
    <>
      <div className={styles.top}>
        <ToneChip tone="interview">Round decision</ToneChip>
        {item.isOverdue && (
          <ToneChip tone="urgent">Overdue</ToneChip>
        )}
      </div>
      <span className={styles.itemTitle}>
        {item.candidateName} — {item.roundName}
      </span>
      <span className={styles.meta}>
        {item.jobTitle} · scorecards complete {formatRelative(item.waitingSince)}
      </span>
      <div className={styles.actions}>
        <Button variant="solid" icon={Gavel} onClick={() => void navigate({ to: '/interviews/$interviewId/debrief', params: { interviewId: item.interviewId } })}>
          Decide
        </Button>
      </div>
    </>
  );
}

// --- An offer to approve (S23: N-48) ----------------------------------------------------

function OfferApprovalBody({ item }: { readonly item: HmOfferApprovalAction }): ReactNode {
  const navigate = useNavigate();
  return (
    <>
      <div className={styles.top}>
        <span className={styles.ref}>{item.reference}</span>
        <ToneChip tone="offer">Offer to approve</ToneChip>
        {item.isOverdue && (
          <ToneChip tone="urgent">Overdue</ToneChip>
        )}
      </div>
      <span className={styles.itemTitle}>{item.candidateName}</span>
      <span className={styles.meta}>
        {item.jobTitle} · {item.stepName} · waiting {formatRelative(item.waitingSince)}
      </span>
      <div className={styles.actions}>
        <Button variant="solid" icon={FileSignature} onClick={() => void navigate({ to: '/offers/$offerId', params: { offerId: item.offerId } })}>
          Review the offer
        </Button>
      </div>
    </>
  );
}

// --- A shortlist to answer (R6: N-88, N-89, N-90) ----------------------------------------

function ShortlistBody({ item }: { readonly item: HmShortlistAction }): ReactNode {
  const navigate = useNavigate();
  return (
    <>
      <div className={styles.top}>
        <span className={styles.ref}>{item.reference}</span>
        <ToneChip tone="screening">Shortlist</ToneChip>
        {item.isOverdue && (
          <ToneChip tone="urgent">Reminded</ToneChip>
        )}
      </div>
      <span className={styles.itemTitle}>{item.jobTitle}</span>
      <span className={styles.meta}>
        {item.awaiting} {item.awaiting === 1 ? 'person waits' : 'people wait'} for your yes or no · sent {formatRelative(item.waitingSince)}
      </span>
      <div className={styles.actions}>
        <Button variant="solid" icon={ListChecks} onClick={() => void navigate({ to: '/my/shortlists/$jobId', params: { jobId: item.jobId } })}>
          Answer
        </Button>
      </div>
    </>
  );
}

// --- An approval (F4.03–F4.05) ------------------------------------------------------

function ApprovalBody({ item }: { readonly item: HmApprovalAction }): ReactNode {
  const decide = useDecideApproval();
  const timer = useActionTimer();
  const showToast = useToasts((state) => state.show);
  const [isDeciding, setIsDeciding] = useState(false);

  async function approve(): Promise<void> {
    try {
      await decide.mutateAsync({ stepId: item.stepId, version: item.stepVersion, decision: 'approve' });
      timer.finish('approve', item.requisitionId);
      showToast({ tone: 'success', message: `${item.reference} approved`, detail: item.stepName });
    } catch (cause) {
      showToast({ tone: 'danger', message: `${item.reference} was not approved`, detail: errorMessage(cause) });
    }
  }

  return (
    <>
      <CardTop item={item}>
        <ToneChip tone="requisition">Approval</ToneChip>
      </CardTop>
      <span className={styles.meta}>
        {item.slotsRequested} {item.slotsRequested === 1 ? 'hire' : 'hires'} · {item.departmentName}
        {item.targetStartDate ? ` · start ${formatIsoDate(item.targetStartDate)}` : ''}
        {item.requestedByName ? ` · raised by ${item.requestedByName}` : ''}
        {item.jevBudgetMicroUsd === null ? '' : ` · Jev budget ${jevBudgetLabel(item.jevBudgetMicroUsd)}`}
      </span>
      {item.justification && <p className={styles.quote}>{item.justification}</p>}
      <span className={styles.meta}>
        {item.stepName} · waiting {formatRelative(item.waitingSince)}
        {item.onBehalfOfName ? ` · covering for ${item.onBehalfOfName}` : ''}
      </span>
      <SlaFigure sla={item.sla} showLimit />
      <div className={styles.actions}>
        <Button variant="solid" icon={Check} onClick={() => void approve()}>
          Approve
        </Button>
        <Button variant="outline" icon={MessageSquareWarning} onClick={() => setIsDeciding(true)}>
          {item.jevBudgetMicroUsd === null ? 'Reject or send back' : 'Change amount, reject or send back'}
        </Button>
      </div>
      {isDeciding && (
        <DecisionDialog
          stepId={item.stepId}
          stepVersion={item.stepVersion}
          reference={item.reference}
          title={item.stepName}
          onBehalfOfName={item.onBehalfOfName}
          jevBudgetMicroUsd={item.jevBudgetMicroUsd}
          onDecided={(decision) => timer.finish(decision, item.requisitionId)}
          onClose={() => setIsDeciding(false)}
        />
      )}
    </>
  );
}

// --- The manager's own request -------------------------------------------------------

const REQUEST_LABELS: Readonly<Record<HmRequestAction['kind'], string>> = {
  finish_draft: 'Draft',
  sent_back: 'Sent back',
  rejected: 'Rejected',
};

function RequestBody({ item }: { readonly item: HmRequestAction }): ReactNode {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const act = useRequisitionAction();
  const timer = useActionTimer();
  const showToast = useToasts((state) => state.show);
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  function edit(): void {
    void navigate({ to: '/request-hire/$requisitionId', params: { requisitionId: item.requisitionId } });
  }

  /** F4.07: version N+1 as a draft, then straight into the form to change it. */
  async function revise(): Promise<void> {
    try {
      await act.mutateAsync({ id: item.requisitionId, action: 'revise', version: item.requisitionVersion });
      timer.finish('revise', item.requisitionId);
      edit();
    } catch (cause) {
      showToast({ tone: 'danger', message: `${item.reference} was not reopened`, detail: errorMessage(cause) });
    }
  }

  async function withdraw(reason: string): Promise<void> {
    const previous = removeHmAction(queryClient, item.key);
    setIsWithdrawing(false);
    try {
      await act.mutateAsync({
        id: item.requisitionId,
        action: 'cancel',
        version: item.requisitionVersion,
        ...(reason ? { reason } : {}),
      });
      timer.finish('withdraw', item.requisitionId);
      showToast({ tone: 'success', message: `${item.reference} withdrawn` });
    } catch (cause) {
      if (previous) queryClient.setQueryData(HM_ACTIONS_KEY, previous);
      showToast({ tone: 'danger', message: `${item.reference} was not withdrawn`, detail: errorMessage(cause) });
    }
  }

  return (
    <>
      <CardTop item={item}>
        <ToneChip tone={item.kind === 'rejected' ? 'rejected' : 'requisition'}>
          {item.isBackfill && item.kind === 'finish_draft' ? 'Backfill draft' : REQUEST_LABELS[item.kind]}
        </ToneChip>
      </CardTop>
      <span className={styles.meta}>
        {item.slotsRequested} {item.slotsRequested === 1 ? 'hire' : 'hires'} · {item.departmentName} ·{' '}
        {item.kind === 'finish_draft' ? 'started' : 'returned'} {formatRelative(item.waitingSince)}
      </span>
      {item.reason && <p className={styles.quote}>“{item.reason}”</p>}
      <div className={styles.actions}>
        {item.kind === 'rejected' ? (
          <Button variant="solid" icon={CornerUpLeft} isPending={act.isPending} onClick={() => void revise()}>
            Revise and resubmit
          </Button>
        ) : (
          <>
            <Button variant="solid" icon={PencilLine} onClick={edit}>
              {item.kind === 'sent_back' ? 'Make the changes' : 'Finish and submit'}
            </Button>
            <Button variant="ghost" icon={Trash2} onClick={() => setIsWithdrawing(true)}>
              Withdraw
            </Button>
          </>
        )}
      </div>
      {isWithdrawing && (
        <ReasonDialog
          title={`Withdraw ${item.reference}?`}
          description="It is closed for good. You can raise a new request at any time."
          label="Why (optional)"
          confirmLabel="Withdraw"
          isDanger
          isReasonOptional
          isPending={act.isPending}
          onConfirm={(reason) => void withdraw(reason)}
          onClose={() => setIsWithdrawing(false)}
        />
      )}
    </>
  );
}
